import type { AccountInfo, AuthenticationResult } from '@azure/msal-browser';

import { MSAL_CONFIG } from '@/constants';
import { parseJwt } from './jwt';

function parseClientInfo(
  clientInfo: unknown,
): { uid?: string; utid?: string } | null {
  if (typeof clientInfo !== 'string' || !clientInfo) {
    return null;
  }
  try {
    const base64 = clientInfo.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const decoded = atob(padded);
    const parsed = JSON.parse(decoded) as { uid?: string; utid?: string };
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Create AccountInfo from token data
 */
export function createAccountFromTokenData(
  tokenData: any,
  authority: string,
): AccountInfo {
  const idTokenClaims = parseJwt(tokenData.id_token);
  let environment = '';
  try {
    environment = new URL(authority).hostname;
  } catch {
    environment = '';
  }
  const clientInfo = parseClientInfo(tokenData.client_info);
  const uid = clientInfo?.uid || idTokenClaims.oid || idTokenClaims.sub || '';
  const utid = clientInfo?.utid || idTokenClaims.tid || '';
  const homeAccountId = uid && utid ? `${uid}.${utid}` : '';
  return {
    homeAccountId,
    environment,
    tenantId: utid,
    username:
      idTokenClaims.preferred_username ||
      idTokenClaims.upn ||
      idTokenClaims.email ||
      '',
    localAccountId: idTokenClaims.oid || '',
    name: idTokenClaims.name || '',
    idToken: tokenData.id_token,
    idTokenClaims: idTokenClaims,
  };
}

/**
 * Create AuthenticationResult from token exchange response
 */
export function createAuthenticationResult(
  tokenData: any,
  account: AccountInfo,
  authority: string,
): AuthenticationResult {
  return {
    account: account,
    accessToken: tokenData.access_token,
    idToken: tokenData.id_token,
    idTokenClaims: account.idTokenClaims || {},
    scopes: tokenData.scope?.split(' ') || [MSAL_CONFIG.SCOPES.BASIC],
    expiresOn: tokenData.expires_in
      ? new Date(
          Date.now() +
            tokenData.expires_in * MSAL_CONFIG.TIME.MILLISECONDS_PER_SECOND,
        )
      : null,
    tenantId: account.tenantId,
    uniqueId: account.localAccountId,
    tokenType: MSAL_CONFIG.TOKEN.TYPE,
    authority: authority,
    correlationId: '',
    requestId: '',
    extExpiresOn: undefined,
    fromCache: false,
  };
}

/**
 * Prefer idToken / idTokenClaims from MSAL's AuthenticationResult (top-level) on the account object.
 * `setUser(account, msalAccessToken, …)` stores `account.idToken` as `user.token` (API) and
 * `msalAccessToken` as `user.access_token` (Microsoft Graph) — different audiences.
 */
export function accountWithMsalIdToken(
  result: AuthenticationResult,
): AccountInfo {
  const base = result.account;
  if (!base) {
    throw new Error('MSAL result missing account');
  }
  return {
    ...base,
    idToken: result.idToken || base.idToken,
    idTokenClaims:
      result.idTokenClaims && Object.keys(result.idTokenClaims).length > 0
        ? (result.idTokenClaims as AccountInfo['idTokenClaims'])
        : base.idTokenClaims,
  };
}
